import Swal from 'sweetalert2';

/**
 * Step Blocking Modal Utilities
 *
 * Reusable SweetAlert2 modal configurations for step-based access control.
 * Provides consistent user feedback when steps are blocked.
 */

/**
 * Show a generic step blocked modal
 * @param {string} reason - User-facing error message
 * @param {string} redirectTo - Path to redirect to after modal closes
 * @param {Function} navigate - React Router navigate function
 */
export const showStepBlockedModal = async (reason, redirectTo, navigate) => {
  await Swal.fire({
    title: 'This action is not available.',
    text: reason,
    icon: 'warning',
    confirmButtonText: 'OK',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });

  if (navigate && redirectTo) {
    navigate(redirectTo);
  }
};

/**
 * Show modal for incomplete profile
 * @param {Function} navigate - React Router navigate function
 */
export const showProfileIncompleteModal = async (navigate) => {
  await Swal.fire({
    title: 'Profile Incomplete',
    text: 'Please complete your profile before accessing the tax questionnaire',
    icon: 'warning',
    confirmButtonText: 'Go to Profile',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });

  if (navigate) {
    navigate('/profile');
  }
};

/**
 * Show modal for questionnaire requirement
 * @param {Function} navigate - React Router navigate function
 */
export const showQuestionnaireRequiredModal = async (navigate) => {
  await Swal.fire({
    title: 'Questionnaire Required',
    text: 'Please complete the tax questionnaire before uploading documents',
    icon: 'warning',
    confirmButtonText: 'Go to Questionnaire',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });

  if (navigate) {
    navigate('/tax-questionnaire');
  }
};

/**
 * Show modal for dependent confirmation requirement
 * @param {Function} navigate - React Router navigate function
 */
export const showDependentsRequiredModal = async (navigate) => {
  await Swal.fire({
    title: 'Dependents Confirmation Required',
    text: 'Please confirm your dependents information before proceeding to the questionnaire',
    icon: 'warning',
    confirmButtonText: 'Update Profile',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });

  if (navigate) {
    navigate('/profile');
  }
};

/**
 * Show modal for banking information requirement
 * @param {Function} navigate - React Router navigate function
 */
export const showBankInfoRequiredModal = async (navigate) => {
  await Swal.fire({
    title: 'Banking Information Required',
    text: 'Please update your banking information before proceeding',
    icon: 'warning',
    confirmButtonText: 'Update Profile',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });

  if (navigate) {
    navigate('/profile');
  }
};

/**
 * Show modal for documents requirement
 * @param {number} finishedCount - Number of uploaded documents
 * @param {number} documentsCount - Total required documents
 * @param {Function} navigate - React Router navigate function
 */
export const showDocumentsRequiredModal = async (finishedCount, documentsCount, navigate) => {
  await Swal.fire({
    title: 'Documents Required',
    html: `
      <p>Please upload all required documents before filing.</p>
      <p class="mt-2"><strong>${finishedCount} of ${documentsCount}</strong> documents uploaded</p>
    `,
    icon: 'warning',
    confirmButtonText: 'Upload Documents',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });

  if (navigate) {
    navigate('/upload-documents');
  }
};

/**
 * Show loading modal while checking access
 * @returns {Object} Swal instance (can be closed with Swal.close())
 */
export const showLoadingModal = () => {
  return Swal.fire({
    title: 'Checking Access',
    text: 'Please wait...',
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });
};

/**
 * Show success modal for step completion
 * @param {string} stepName - Name of the completed step
 */
export const showStepCompletedModal = async (stepName) => {
  await Swal.fire({
    title: 'Step Complete!',
    text: `You have successfully completed: ${stepName}`,
    icon: 'success',
    confirmButtonText: 'Continue',
    confirmButtonColor: '#198754'
  });
};
